import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import { isInwardSupplyRow } from "../../../services/billingImportFindService";
import {
  normalizeBrand,
  oeBrandFromBrandField,
} from "../../../services/monthlyAutosReportService";
import {
  lineSaleAmount,
  loadLineChunkRowsForDateRange,
  normalizeVoucherType,
} from "./salesLineChunksReader";
import {
  normalizeSalesImportBranchId,
  salesImportBranchDisplayName,
  sortSalesImportBranchIds,
} from "../config/salesImportBranchIds";
import { rowTranDateYmd } from "../utils/dateParse";

function pickMasterName(data, ...fields) {
  for (const field of fields) {
    const value = String(data?.[field] ?? "").trim();
    if (value) return value;
  }
  return "";
}

export async function loadMasterBrandNames() {
  const snap = await getDocs(collection(db, "brands"));
  const names = snap.docs
    .map((d) => pickMasterName(d.data(), "name", "brandname", "brandName", "Brand"))
    .filter(isValidMasterBrandName);
  return [...new Set(names)].sort((a, b) => a.localeCompare(b));
}

/** part/code/alias (lower) → brand text from items master */
export async function loadItemBrandByPartNo() {
  const snap = await getDocs(collection(db, "items"));
  const map = new Map();
  for (const d of snap.docs) {
    const data = d.data() || {};
    const brand = pickMasterName(data, "brand", "brandName", "brandname", "Brand");
    if (!isValidMasterBrandName(brand)) continue;
    for (const codeField of [
      data.code,
      data.partno,
      data.partNo,
      data.alias,
      data.barcode,
      data.productid,
      data.productId,
    ]) {
      const code = String(codeField ?? "").trim().toLowerCase();
      if (code) map.set(code, brand);
    }
  }
  return map;
}

export async function loadMasterGroupNames() {
  const snap = await getDocs(collection(db, "groups"));
  const names = snap.docs
    .map((d) => pickMasterName(d.data(), "name", "gname", "group", "groupName", "Group"))
    .filter(Boolean);
  return [...new Set(names)].sort((a, b) => a.localeCompare(b));
}

function isValidMasterBrandName(name) {
  const s = String(name ?? "").trim();
  if (!s) return false;
  return /[A-Z0-9]/i.test(s);
}

function isOeBrandKey(key) {
  return /^oe\s+/i.test(String(key ?? "").trim());
}

function baseKeyFromOeBrand(key) {
  const m = String(key ?? "").trim().match(/^oe\s+(.+)$/i);
  return m ? m[1].trim() : "";
}

function findOeCompanionKey(baseKey, masterKeys) {
  const baseUpper = String(baseKey ?? "").trim().toUpperCase();
  if (!baseUpper) return "";
  for (const master of masterKeys || []) {
    const upper = String(master).trim().toUpperCase();
    if (upper === `OE ${baseUpper}` || upper === `OE${baseUpper}`) return master;
  }
  return "";
}

function buildBrandDisplayKeys(masterKeys, combineOeIntoBase) {
  const valid = (masterKeys || []).filter(isValidMasterBrandName);
  if (!combineOeIntoBase) return [...valid].sort((a, b) => a.localeCompare(b));

  const validUpper = new Set(valid.map((k) => k.toUpperCase()));
  return valid
    .filter((key) => {
      if (!isOeBrandKey(key)) return true;
      const base = baseKeyFromOeBrand(key);
      return !base || !validUpper.has(base.toUpperCase());
    })
    .sort((a, b) => a.localeCompare(b));
}

function sumCellsForBrandKey(key, branchIds, cellMap, masterKeys, combineOeIntoBase) {
  const keysToSum = [key];
  if (combineOeIntoBase && !isOeBrandKey(key)) {
    const oeKey = findOeCompanionKey(key, masterKeys);
    if (oeKey) keysToSum.push(oeKey);
  }

  const byBranch = {};
  let totalSales = 0;
  let totalPurchase = 0;
  for (const br of branchIds) {
    let salesAmount = 0;
    let purchaseAmount = 0;
    for (const brandKey of keysToSum) {
      const c = cellMap.get(`${brandKey}::${br}`) || { salesAmount: 0, purchaseAmount: 0 };
      salesAmount += c.salesAmount;
      purchaseAmount += c.purchaseAmount;
    }
    byBranch[br] = { salesAmount, purchaseAmount };
    totalSales += salesAmount;
    totalPurchase += purchaseAmount;
  }
  return { byBranch, totalSales, totalPurchase };
}

function buildBrandLookup(masterBrands) {
  const lookup = new Map();
  for (const name of masterBrands) {
    if (!isValidMasterBrandName(name)) continue;
    const lower = name.toLowerCase();
    const compact = lower.replace(/\s+/g, "");
    lookup.set(lower, name);
    lookup.set(compact, name);
    lookup.set(normalizeBrand(name).toLowerCase(), name);
    if (/^oe\s/i.test(name)) {
      const stripped = name.replace(/^oe\s+/i, "");
      lookup.set(stripped.toLowerCase(), name);
      lookup.set(stripped.replace(/\s+/g, "").toLowerCase(), name);
    }
  }
  return lookup;
}

function buildGroupLookup(masterGroups) {
  const lookup = new Map();
  for (const name of masterGroups) {
    lookup.set(name.toLowerCase(), name);
  }
  return lookup;
}

const NON_BRAND_HINTS = new Set([
  "CREDIT",
  "CASH",
  "DEBIT",
  "UDHAR",
  "SALE",
  "PURCHASE",
  "SALES",
]);

function isUsableBrandText(value) {
  const s = String(value ?? "").trim();
  if (!s) return false;
  return !NON_BRAND_HINTS.has(s.toUpperCase());
}

function partLookupKeysFromRow(row) {
  const keys = [];
  for (const field of [
    row?.partNo,
    row?.partno,
    row?.alias,
    row?.productId,
    row?.productid,
    row?.["Product ID"],
  ]) {
    const s = String(field ?? "").trim().toLowerCase();
    if (s) keys.push(s);
  }
  return keys;
}

function brandFromItemsMaster(row, itemBrandByPartNo) {
  if (!itemBrandByPartNo) return "";
  for (const key of partLookupKeysFromRow(row)) {
    const brand = itemBrandByPartNo.get(key);
    if (brand) return brand;
  }
  return "";
}

function collectBrandTextCandidates(row, itemBrandByPartNo) {
  const out = [];
  const seen = new Set();
  const push = (value) => {
    const s = String(value ?? "").trim();
    if (!isUsableBrandText(s)) return;
    const key = s.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  };

  const uploadedBrand = rawBrandFromRow(row);
  const fromItems = brandFromItemsMaster(row, itemBrandByPartNo);

  if (uploadedBrand) {
    push(uploadedBrand);
    if (fromItems) push(fromItems);
    for (const field of [
      row?.profitCentre,
      row?.profitCentreName,
      row?.itemGroup,
      row?.itemgroup,
      row?.["Item Group"],
      row?.alias,
      row?.itemName,
    ]) {
      push(field);
    }
    return out;
  }

  // BrandName column missing on upload — resolve from products master, then Item Group / line text
  if (fromItems) push(fromItems);
  for (const field of [
    row?.itemGroup,
    row?.itemgroup,
    row?.["Item Group"],
    row?.alias,
    row?.itemName,
    row?.profitCentre,
    row?.profitCentreName,
  ]) {
    push(field);
  }

  return out;
}

function matchTextToMasterBrand(text, masterBrands, brandLookup) {
  if (!text) return null;
  const trimmed = String(text).trim();
  if (!isValidMasterBrandName(trimmed)) return null;
  const lower = trimmed.toLowerCase();
  const compact = lower.replace(/\s+/g, "");

  if (brandLookup.has(lower)) return brandLookup.get(lower);
  if (brandLookup.has(compact)) return brandLookup.get(compact);

  for (const master of masterBrands) {
    if (!isValidMasterBrandName(master)) continue;
    if (master.toLowerCase() === lower) return master;
  }

  const oeBrand = oeBrandFromBrandField(trimmed);
  if (oeBrand) {
    for (const master of masterBrands) {
      const upper = master.toUpperCase();
      if (upper === `OE ${oeBrand}` || upper === `OE${oeBrand}` || upper === oeBrand) return master;
    }
  }

  const textUpper = trimmed.toUpperCase();
  const byLength = [...masterBrands].sort((a, b) => b.length - a.length);
  for (const master of byLength) {
    const masterUpper = master.toUpperCase();
    if (!masterUpper) continue;
    if (textUpper === masterUpper) return master;
    if (textUpper.includes(masterUpper) || masterUpper.includes(textUpper)) return master;
  }

  const normalized = normalizeBrand(trimmed);
  if (normalized !== "MISCELLANEOUS") {
    for (const master of masterBrands) {
      if (master.toUpperCase() === normalized || normalizeBrand(master) === normalized) return master;
    }
  }

  return null;
}

function resolveRowToMasterBrand(row, masterBrands, brandLookup, itemBrandByPartNo) {
  for (const candidate of collectBrandTextCandidates(row, itemBrandByPartNo)) {
    const matched = matchTextToMasterBrand(candidate, masterBrands, brandLookup);
    if (matched) return matched;
  }
  return null;
}

function rawGroupFromRow(row) {
  return String(row.itemGroup ?? row.itemgroup ?? row["Item Group"] ?? "").trim();
}

function rawBrandFromRow(row) {
  return String(row.brandName ?? row.BrandName ?? row.brandname ?? row.brand ?? row["Brand Name"] ?? "").trim();
}

function describeUnmappedRow(row, dimension, itemBrandByPartNo = null) {
  const partNo = String(row?.partNo ?? row?.partno ?? "").trim();
  const voucherType = normalizeVoucherType(row);
  const tranDate = rowTranDateYmd(row) || "";
  const voucherNo = String(row?.voucherNo ?? row?.billNo ?? "").trim();
  const amount = lineSaleAmount(row);

  if (dimension === "group") {
    const itemGroup = rawGroupFromRow(row);
    const brandName = rawBrandFromRow(row);
    const sources = [];
    if (itemGroup) sources.push(`Item Group: ${itemGroup}`);
    else sources.push("Item Group: (empty)");
    if (brandName) sources.push(`BrandName: ${brandName}`);
    else sources.push("BrandName: (not uploaded)");
    return {
      label: itemGroup || "(empty Item Group)",
      source: sources.join(" · "),
      partNo,
      voucherType,
      tranDate,
      voucherNo,
      amount,
      brandName,
      itemGroup,
    };
  }

  const brandName = rawBrandFromRow(row);
  const itemGroup = rawGroupFromRow(row);
  const itemsBrand = brandFromItemsMaster(row, itemBrandByPartNo);
  const profitCentre = String(row?.profitCentre ?? row?.profitCentreName ?? "").trim();
  const alias = String(row?.alias ?? "").trim();
  const itemName = String(row?.itemName ?? "").trim();
  const parts = [];
  if (brandName) parts.push(`BrandName: ${brandName}`);
  else parts.push("BrandName: (not uploaded)");
  if (itemsBrand) parts.push(`Products master brand: ${itemsBrand}`);
  else if (partNo) parts.push(`Products master: no brand for Part No ${partNo}`);
  if (itemGroup) parts.push(`Item Group: ${itemGroup}`);
  if (profitCentre) parts.push(`Profit Centre: ${profitCentre}`);
  if (alias) parts.push(`Alias: ${alias}`);
  if (itemName) parts.push(`Item Name: ${itemName}`);
  if (partNo) parts.push(`Part No: ${partNo}`);

  const primary =
    itemsBrand ||
    brandName ||
    itemGroup ||
    profitCentre ||
    alias ||
    itemName ||
    (partNo ? `(part ${partNo}, no master brand)` : "");

  return {
    label: primary || "(no brand text on line)",
    source: parts.join(" · "),
    partNo,
    voucherType,
    tranDate,
    voucherNo,
    amount,
    brandName,
    itemGroup,
    itemsBrand,
  };
}

function buildUnmappedSample(row, dimension, itemBrandByPartNo = null) {
  const info = describeUnmappedRow(row, dimension, itemBrandByPartNo);
  return {
    tranDate: info.tranDate,
    voucherType: info.voucherType,
    voucherNo: info.voucherNo,
    partNo: info.partNo,
    brandName: info.brandName || "",
    itemGroup: info.itemGroup || "",
    amount: info.amount,
    source: info.source,
  };
}

function matchMasterGroup(raw, groupLookup, masterGroups) {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (groupLookup.has(lower)) return groupLookup.get(lower);
  for (const master of masterGroups) {
    if (master.toLowerCase() === lower) return master;
  }
  const textUpper = raw.toUpperCase();
  for (const master of [...masterGroups].sort((a, b) => b.length - a.length)) {
    const masterUpper = master.toUpperCase();
    if (textUpper === masterUpper || textUpper.includes(masterUpper)) return master;
  }
  return null;
}

function resolveRowMasterKey(row, dimension, brandLookup, groupLookup, masterKeys, itemBrandByPartNo) {
  if (dimension === "group") {
    return matchMasterGroup(rawGroupFromRow(row), groupLookup, masterKeys);
  }
  return resolveRowToMasterBrand(row, masterKeys, brandLookup, itemBrandByPartNo);
}

function rowMatchesVoucherType(row, selectedVoucherType) {
  if (!selectedVoucherType || selectedVoucherType === "all") return true;
  return normalizeVoucherType(row) === selectedVoucherType;
}

function branchIdFromRow(row) {
  return normalizeSalesImportBranchId(row.branchId ?? row.BranchID ?? row.branch ?? "") || "Unknown";
}

function pickAmount(cell, amountType) {
  if (amountType === "purchase") return cell.purchaseAmount;
  if (amountType === "net") return cell.salesAmount - cell.purchaseAmount;
  return cell.salesAmount;
}

/**
 * @param {object[]} rows
 * @param {{ year?: number, branchIds?: string[], amountType?: 'sales'|'purchase'|'net', dimension?: 'brand'|'group', masterKeys?: string[], itemBrandByPartNo?: Map<string, string>, voucherType?: string, combineOeIntoBase?: boolean }} [opts]
 */
export function aggregateBrandGroupByBranch(rows, opts = {}) {
  const {
    year,
    branchIds = [],
    amountType = "sales",
    dimension = "brand",
    masterKeys = [],
    itemBrandByPartNo = null,
    voucherType = "all",
    combineOeIntoBase = false,
  } = opts;

  const brandLookup = dimension === "brand" ? buildBrandLookup(masterKeys) : null;
  const groupLookup = dimension === "group" ? buildGroupLookup(masterKeys) : null;
  const branchSet = new Set(
    (branchIds || []).map((id) => normalizeSalesImportBranchId(id)).filter(Boolean)
  );
  const filterBranches = branchSet.size > 0;
  const cellMap = new Map();
  const unmappedByBranch = {};
  const unmappedDetailMap = new Map();
  const voucherTypeOptions = new Set();
  let rowsWithoutBrandName = 0;
  let rowsWithBrandName = 0;
  let rowsResolvedFromItemsMaster = 0;

  for (const row of rows || []) {
    const ymd = rowTranDateYmd(row);
    if (!ymd) continue;
    if (year != null && Number(ymd.slice(0, 4)) !== year) continue;

    const branch = branchIdFromRow(row);
    if (filterBranches && !branchSet.has(branch)) continue;

    voucherTypeOptions.add(normalizeVoucherType(row));
    if (!rowMatchesVoucherType(row, voucherType)) continue;

    if (dimension === "brand") {
      if (rawBrandFromRow(row)) rowsWithBrandName += 1;
      else rowsWithoutBrandName += 1;
    }

    let key = resolveRowMasterKey(
      row,
      dimension,
      brandLookup,
      groupLookup,
      masterKeys,
      itemBrandByPartNo
    );
    if (key && !isValidMasterBrandName(key)) key = null;
    if (!key && !masterKeys.length) {
      if (dimension === "group") {
        key = rawGroupFromRow(row) || "Unknown";
      } else {
        const candidates = collectBrandTextCandidates(row, itemBrandByPartNo);
        const raw = candidates[0] || "";
        if (!raw) key = "Unknown";
        else {
          const normalized = normalizeBrand(raw);
          key = normalized !== "MISCELLANEOUS" ? normalized : raw.toUpperCase();
        }
      }
    }

    const amt = lineSaleAmount(row);
    const isPurchase = isInwardSupplyRow(row);

    if (!key) {
      const info = describeUnmappedRow(row, dimension, itemBrandByPartNo);
      const detailKey = info.label.toLowerCase();
      const prevDetail = unmappedDetailMap.get(detailKey) || {
        label: info.label,
        source: info.source,
        lineCount: 0,
        salesAmount: 0,
        purchaseAmount: 0,
        byBranch: {},
        voucherTypes: {},
        samples: [],
        sampleKeys: new Set(),
      };
      prevDetail.lineCount += 1;
      if (isPurchase) prevDetail.purchaseAmount += amt;
      else prevDetail.salesAmount += amt;

      const branchPrev = prevDetail.byBranch[branch] || {
        salesAmount: 0,
        purchaseAmount: 0,
        lineCount: 0,
      };
      if (isPurchase) branchPrev.purchaseAmount += amt;
      else branchPrev.salesAmount += amt;
      branchPrev.lineCount += 1;
      prevDetail.byBranch[branch] = branchPrev;

      prevDetail.voucherTypes[info.voucherType] = (prevDetail.voucherTypes[info.voucherType] || 0) + 1;

      const sampleKey = `${info.partNo}|${info.voucherNo}|${info.tranDate}|${info.amount}`;
      if (prevDetail.samples.length < 10 && !prevDetail.sampleKeys.has(sampleKey)) {
        prevDetail.sampleKeys.add(sampleKey);
        prevDetail.samples.push(buildUnmappedSample(row, dimension, itemBrandByPartNo));
      }
      unmappedDetailMap.set(detailKey, prevDetail);

      const prev = unmappedByBranch[branch] || { salesAmount: 0, purchaseAmount: 0 };
      if (isPurchase) prev.purchaseAmount += amt;
      else prev.salesAmount += amt;
      unmappedByBranch[branch] = prev;
      continue;
    }

    const cellKey = `${key}::${branch}`;
    const prev = cellMap.get(cellKey) || {
      key,
      branch,
      salesAmount: 0,
      purchaseAmount: 0,
      lineCount: 0,
    };

    if (isPurchase) prev.purchaseAmount += amt;
    else prev.salesAmount += amt;
    prev.lineCount += 1;
    if (dimension === "brand" && !rawBrandFromRow(row) && brandFromItemsMaster(row, itemBrandByPartNo)) {
      rowsResolvedFromItemsMaster += 1;
    }
    cellMap.set(cellKey, prev);
  }

  const branchesFound = new Set();
  for (const c of cellMap.values()) branchesFound.add(c.branch);
  const branchIdsFound = sortSalesImportBranchIds(
    filterBranches ? [...branchSet] : [...branchesFound]
  );

  const keySet = new Set();
  if (masterKeys.length) {
    const displayKeys =
      dimension === "brand"
        ? buildBrandDisplayKeys(masterKeys, combineOeIntoBase)
        : masterKeys.filter(isValidMasterBrandName);
    for (const k of displayKeys) keySet.add(k);
  } else {
    for (const c of cellMap.values()) {
      if (isValidMasterBrandName(c.key)) keySet.add(c.key);
    }
  }
  const keys = [...keySet].sort((a, b) => a.localeCompare(b));

  const tableRows = keys.map((key) => {
    const totals =
      dimension === "brand" && combineOeIntoBase
        ? sumCellsForBrandKey(key, branchIdsFound, cellMap, masterKeys, true)
        : null;
    const byBranch = {};
    let totalSales = 0;
    let totalPurchase = 0;
    if (totals) {
      Object.assign(byBranch, totals.byBranch);
      totalSales = totals.totalSales;
      totalPurchase = totals.totalPurchase;
    } else {
      for (const br of branchIdsFound) {
        const c = cellMap.get(`${key}::${br}`) || { salesAmount: 0, purchaseAmount: 0 };
        byBranch[br] = { salesAmount: c.salesAmount, purchaseAmount: c.purchaseAmount };
        totalSales += c.salesAmount;
        totalPurchase += c.purchaseAmount;
      }
    }
    const oeKey = dimension === "brand" ? findOeCompanionKey(key, masterKeys) : "";
    return {
      key,
      label: key,
      byBranch,
      totalSales,
      totalPurchase,
      totalNet: totalSales - totalPurchase,
      includesOe: Boolean(combineOeIntoBase && oeKey && !isOeBrandKey(key)),
      oeLabel: oeKey || "",
    };
  });

  const unmappedTotals = Object.values(unmappedByBranch).reduce(
    (acc, cell) => {
      acc.salesAmount += cell.salesAmount;
      acc.purchaseAmount += cell.purchaseAmount;
      return acc;
    },
    { salesAmount: 0, purchaseAmount: 0 }
  );
  if (unmappedTotals.salesAmount || unmappedTotals.purchaseAmount) {
    const byBranch = {};
    for (const br of branchIdsFound) {
      byBranch[br] = unmappedByBranch[br] || { salesAmount: 0, purchaseAmount: 0 };
    }
    tableRows.push({
      key: "__unmapped__",
      label: "Unmapped (not in master list)",
      byBranch,
      totalSales: unmappedTotals.salesAmount,
      totalPurchase: unmappedTotals.purchaseAmount,
      totalNet: unmappedTotals.salesAmount - unmappedTotals.purchaseAmount,
      isUnmapped: true,
    });
  }

  const masterRows = tableRows
    .filter((row) => !row.isUnmapped)
    .sort((a, b) => a.label.localeCompare(b.label));
  const unmappedRows = tableRows.filter((row) => row.isUnmapped);
  const orderedRows = [...masterRows, ...unmappedRows];

  const branchTotals = {};
  for (const br of branchIdsFound) {
    let salesAmount = 0;
    let purchaseAmount = 0;
    for (const r of orderedRows) {
      salesAmount += r.byBranch[br]?.salesAmount || 0;
      purchaseAmount += r.byBranch[br]?.purchaseAmount || 0;
    }
    branchTotals[br] = { salesAmount, purchaseAmount, net: salesAmount - purchaseAmount };
  }

  const grandSales = Object.values(branchTotals).reduce((s, t) => s + t.salesAmount, 0);
  const grandPurchase = Object.values(branchTotals).reduce((s, t) => s + t.purchaseAmount, 0);

  const unmappedDetails = [...unmappedDetailMap.values()]
    .map((entry) => {
      const voucherSummary = Object.entries(entry.voucherTypes || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([vt, count]) => `${vt} (${count})`)
        .join(", ");
      return {
        label: entry.label,
        source: entry.source,
        lineCount: entry.lineCount,
        totalSales: entry.salesAmount,
        totalPurchase: entry.purchaseAmount,
        totalNet: entry.salesAmount - entry.purchaseAmount,
        byBranch: entry.byBranch,
        voucherSummary,
        samples: entry.samples,
      };
    })
    .sort(
      (a, b) =>
        pickAmount({ salesAmount: b.totalSales, purchaseAmount: b.totalPurchase }, amountType) -
        pickAmount({ salesAmount: a.totalSales, purchaseAmount: a.totalPurchase }, amountType)
    );

  return {
    rows: orderedRows,
    branches: branchIdsFound.map((id) => ({
      id,
      label: salesImportBranchDisplayName(id),
    })),
    branchTotals,
    grandSales,
    grandPurchase,
    grandNet: grandSales - grandPurchase,
    lineCount: [...cellMap.values()].reduce((s, c) => s + c.lineCount, 0),
    unmappedLineCount: unmappedDetails.reduce((s, d) => s + d.lineCount, 0),
    unmappedDetails,
    rowsWithoutBrandName,
    rowsWithBrandName,
    brandNameMissing: dimension === "brand" && rowsWithoutBrandName >= rowsWithBrandName,
    rowsResolvedFromItemsMaster,
    dimension,
    amountType,
    masterCount: masterKeys.length,
    voucherType,
    voucherTypeOptions: [...voucherTypeOptions].sort((a, b) => a.localeCompare(b)),
    combineOeIntoBase: dimension === "brand" ? combineOeIntoBase : false,
  };
}

/**
 * @param {{ year: number, branchIds?: string[], dimension?: string, amountType?: string }} params
 * @param {(n: number) => void} [onProgress]
 */
export async function fetchBrandGroupBranchReport(params, onProgress) {
  const year = Number(params?.year);
  if (!Number.isFinite(year)) throw new Error("Select a valid year.");

  const dimension = params.dimension || "brand";
  const masterKeys =
    dimension === "group" ? await loadMasterGroupNames() : await loadMasterBrandNames();
  const itemBrandByPartNo =
    dimension === "brand" ? await loadItemBrandByPartNo() : null;

  const fromDate = `${year}-01-01`;
  const toDate = `${year}-12-31`;
  const { rows } = await loadLineChunkRowsForDateRange({ fromDate, toDate }, (n) => {
    onProgress?.(n);
  });

  return {
    ...aggregateBrandGroupByBranch(rows, {
      year,
      branchIds: params.branchIds,
      dimension,
      amountType: params.amountType || "sales",
      masterKeys,
      itemBrandByPartNo,
      voucherType: params.voucherType || "all",
      combineOeIntoBase: params.combineOeIntoBase === true,
    }),
    year,
    fromDate,
    toDate,
    masterKeys,
  };
}

/** Flat rows for Excel export. */
export function flattenBrandGroupBranchForExport(report) {
  const dimLabel = report.dimension === "group" ? "Group" : "Brand";
  const amountLabel =
    report.amountType === "purchase"
      ? "Purchase"
      : report.amountType === "net"
        ? "Net (Sales − Purchase)"
        : "Sales";

  const out = [];
  for (const r of report?.rows || []) {
    const row = {
      [dimLabel]: r.label,
      [`Total ${amountLabel}`]: pickAmount(
        { salesAmount: r.totalSales, purchaseAmount: r.totalPurchase },
        report.amountType
      ),
    };
    for (const br of report.branches || []) {
      const cell = r.byBranch[br.id] || { salesAmount: 0, purchaseAmount: 0 };
      row[br.label] = pickAmount(cell, report.amountType);
    }
    out.push(row);
  }

  const totalRow = { [dimLabel]: "TOTAL" };
  for (const br of report.branches || []) {
    const t = report.branchTotals?.[br.id] || { salesAmount: 0, purchaseAmount: 0 };
    totalRow[br.label] = pickAmount(t, report.amountType);
  }
  totalRow[`Total ${amountLabel}`] = pickAmount(
    { salesAmount: report.grandSales, purchaseAmount: report.grandPurchase },
    report.amountType
  );
  out.push(totalRow);
  return out;
}

/** Flat rows for unmapped detail Excel export. */
export function flattenUnmappedDetailsForExport(report) {
  const dimLabel = report.dimension === "group" ? "Import Group Text" : "Import Brand Text";
  const amountLabel =
    report.amountType === "purchase"
      ? "Purchase"
      : report.amountType === "net"
        ? "Net (Sales − Purchase)"
        : "Sales";

  const summaryRows = (report?.unmappedDetails || []).map((d) => {
    const row = {
      [dimLabel]: d.label,
      "Source fields on import line": d.source,
      Lines: d.lineCount,
      "Voucher types (top)": d.voucherSummary || "",
      [`Total ${amountLabel}`]: pickAmount(
        { salesAmount: d.totalSales, purchaseAmount: d.totalPurchase },
        report.amountType
      ),
    };
    for (const br of report.branches || []) {
      const cell = d.byBranch?.[br.id] || { salesAmount: 0, purchaseAmount: 0 };
      row[br.label] = pickAmount(cell, report.amountType);
    }
    return row;
  });

  const sampleRows = [];
  for (const d of report?.unmappedDetails || []) {
    for (const s of d.samples || []) {
      sampleRows.push({
        [dimLabel]: d.label,
        "Tran Date": s.tranDate,
        "Voucher Type": s.voucherType,
        "Voucher No": s.voucherNo,
        "Part No": s.partNo,
        BrandName: s.brandName,
        "Item Group": s.itemGroup,
        Amount: s.amount,
        "Line fields": s.source,
      });
    }
  }

  return { summaryRows, sampleRows };
}

export { pickAmount };
